"use client"

import { useLocale, useTranslations } from "next-intl"
import { Controller, FieldPath, FieldValues, useFormContext } from "react-hook-form"

import { TCounter } from "../../ui/field/TCounter"
import { TTextareaControlled } from "../../ui/input/TTextareaControlled"
import { TTextInput } from "../../ui/input/TTextInput"
import { FormControl, FormItem, FormMessage } from "../../ui/shadcn/form"
import { useDynamicFormLabelContext } from "../components/form-label-context"
import { DynamicFormLabel } from "../components/form-label"
import type { ParsedField } from "../types"
import { toTitleCase } from "../utils/format-label"
import { tWithFallback } from "../utils/i18n"

interface StringFieldProps {
	field: ParsedField
}

export function StringField({ field: parsedField }: StringFieldProps) {
	const t = useTranslations()
	const locale = useLocale()
	const { namespace } = useDynamicFormLabelContext()
	const { name, schema, required } = parsedField
	const description = schema.description
	const title = schema.title
	const { control } = useFormContext()
	// Use textarea if explicitly specified via x-ui-component or if maxLength > 100
	const useTextarea = schema["x-ui-component"] === "textarea" || (schema.maxLength && schema.maxLength > 100)

	// Extended UI options
	const showCounter = schema["x-ui-show-counter"] === true
	const maxChars = (schema["x-ui-max-chars"] as number) || schema.maxLength
	const spellCheck = schema["x-ui-spell-check"] === true
	const onFocus = schema["x-ui-on-focus"]
	const defaultValue = schema.default !== undefined ? (schema.default as string) : ""

	const defaultPlaceholder = t("creagen-components.fields.string-field.placeholder.enterValue", { fieldName: toTitleCase(name) })
	const placeholder = tWithFallback(t, locale, `${namespace}.fields.${name}.placeholder`, defaultPlaceholder)
	return (
		<div className={useTextarea ? "relative h-[180px] shrink-0" : undefined}>
			<Controller
				name={name as FieldPath<FieldValues>}
				control={control}
				render={({ field, fieldState }) => (
					<FormItem className={useTextarea ? "h-full" : undefined}>
						<FormControl>
							{useTextarea ? (
								<TTextareaControlled
									label={<DynamicFormLabel name={name} title={title} required={required} description={description} />}
									name={name}
									value={field.value ?? defaultValue}
									onChange={field.onChange}
									onBlur={field.onBlur}
									onFocus={onFocus}
									spellCheck={spellCheck}
									error={!!fieldState.error}
									placeholder={placeholder}
									renderInnerContent={
										showCounter && maxChars ? (
											<TCounter value={field.value ?? defaultValue} maxChar={maxChars} error={!!fieldState.error} className="text-base!" />
										) : undefined
									}
									className="group h-full"
								/>
							) : (
								<TTextInput
									label={<DynamicFormLabel name={name} title={title} required={required} description={description} />}
									{...field}
									value={field.value ?? defaultValue}
									placeholder={placeholder}
									onFocus={onFocus}
									className="group"
								/>
							)}
						</FormControl>
						<FormMessage />
					</FormItem>
				)}
			/>
		</div>
	)
}
